import { Injectable, ViewChild } from '@angular/core';
import { LoyaltySite } from "./loyalty-site";
import { Observable } from "rxjs/Observable";
import { DatePipe } from '@angular/common';
import { SearchTableState } from "../search-table-state";
import { BaseService } from "../base.service";
import { HttpClient, HttpParams, HttpBackend } from "@angular/common/http";
import { LocationGroup } from '../../location-group/location-group';
import { Provinces } from '../../provinces/provinces';
import { DynamicFormComponent } from "../../dynamic-form/dynamic-form.component";

@Injectable()
export class LoyaltySiteManagementService extends BaseService {

    private static searchUrl: string = '/api/sites/search';
    private static exportExcelUrl: string = '/api/sites/exportExcel';
    public static loyaltySiteUrl: string = '/api/sites';
    private static postDisableLoyaltySiteUrl: string = '/api/sites/disableSite';
    private static nextLocationIdUrl: string = '/api/sites/getNextLocationId';
    private static getNewPasswordUrl: string = '/api/sites/getNewPassword';
    private static getNextUsernameUrl: string = '/api/sites/getNextUsername';
    private static provincesUrl: string ='/api/sites/getProvinces';
    private static locationGroupsUrl: string ='/api/sites/getLocationGroups';
    public searchTableState: SearchTableState = null;

    @ViewChild('loyaltySiteForm') loyaltySiteFormComponent: DynamicFormComponent;

    constructor(
        private http: HttpClient,
        private datePipe: DatePipe,
        private httpClient: HttpClient, 
        handler: HttpBackend
    ) {
        super();
        this.searchTableState = new SearchTableState();
        this.searchTableState.propertyName = 'CreateDateUtc';
        this.httpClient = new HttpClient(handler);
    }

    private searchTableStateCopy(): any {
        let copy: any = Object.assign({}, this.searchTableState);
        /* Disable refresh search */
        copy.lastRefreshed = new Date();
        if (copy.lastRefreshed) {
            copy.lastRefreshed = new Date(copy.lastRefreshed.getTime() + -1 * (new Date().getTimezoneOffset() * 60000));
        }
        this.updateFilterField(copy, 'siteName', 'siteNameCondition');
        return copy;
    }

    public search(): Observable<Array<LoyaltySite>> {
        let copy = this.searchTableStateCopy();
        return this.http.post<any>(LoyaltySiteManagementService.searchUrl, copy)
            .map(data => {
                let loyaltySites: Array<LoyaltySite> = this.dataToLoyaltySite(data);
                this.searchTableState.totalItems = parseInt(loyaltySites.length ? data[0].Total : 0);
                return loyaltySites;
            });
    }

    public exportExcel(): Observable<any> {
        let copy = this.searchTableStateCopy();
        return this.http.post<any>(LoyaltySiteManagementService.exportExcelUrl, copy, { responseType: 'blob' as 'json' });
    }


    public dataToLoyaltySite(data: any): Array<LoyaltySite> {
        let loyaltySites = new Array<LoyaltySite>();
        if (data == null) {
            return loyaltySites;
        }
        for (let d of data) {
            let loyaltySite = new LoyaltySite();
            loyaltySite.LoyaltySiteId = d.LoyaltySiteId;
            loyaltySite.siteId = d.SiteId;
            loyaltySite.SiteName = d.SiteName;
            loyaltySite.userName = d.UserName;
            loyaltySite.createDateUtc = this.datePipe.transform(new Date(d.CreateDateUtc), 'yyyy-MM-dd HH:mm');
            loyaltySite.status = d.Status == 2;
            loyaltySites.push(loyaltySite);
        }
        return loyaltySites;
    }

    public getLoyaltySite(id: string): Observable<LoyaltySite> {
        let params = new HttpParams()
            .set('id', id);
        return this.http.get<any>(LoyaltySiteManagementService.loyaltySiteUrl, { params: params })
            .map(data => {
                let loyaltySite = new LoyaltySite();
                loyaltySite.LoyaltySiteId = data.LoyaltySiteId;
                loyaltySite.siteId = data.SiteId;
                loyaltySite.SiteName = data.SiteName;
                loyaltySite.userName = data.UserName;
                loyaltySite.plainTextPassword = data.PlainTextPassword;
                loyaltySite.password = data.password;
                loyaltySite.confirmPassword = data.confirmPassword;
                loyaltySite.address1 = data.Address1;
                loyaltySite.address2 = data.Address2;
                loyaltySite.suburb = data.Suburb;
                loyaltySite.postalCode = data.PostalCode;
                loyaltySite.email = data.Email;
                loyaltySite.manager = data.Manager;
                loyaltySite.openingTime = parseInt(data.openingTime);
                loyaltySite.closingTime = parseInt(data.closingTime);
                loyaltySite.latitude = parseFloat(data.Latitude);
                loyaltySite.longitude = parseFloat(data.Longitude);
                loyaltySite.mobileNumber = data.MobileNumber;
                loyaltySite.city = data.City;
                loyaltySite.CurrentId = data.CurrentId;
                loyaltySite.LocationGroupID = data.LocationGroupID.toString();
                loyaltySite.TradingHours = data.TradingHours;
                loyaltySite.POS = data.Pos;
                loyaltySite.LocationType = data.LocationType;
                if (data.ProvinceID !== null) {
                    loyaltySite.provinceID = data.ProvinceID.toString();
                }
                loyaltySite.country = data.Country;
                loyaltySite.status = data.Status == 2;
                return loyaltySite;
            });
    }
   
    public getAllProvinces(): Observable<any> { 
        return this.http.get<any>(LoyaltySiteManagementService.provincesUrl);
    }

    public getAllLocationGroups(): Observable<any> { 
        return this.http.get<any>(LoyaltySiteManagementService.locationGroupsUrl);
    }

    public disableLoyaltySite(loyaltySiteId: string, disabled: boolean): Observable<any> {
        let params = new HttpParams()
            .set('id', loyaltySiteId)
            .set('disabled', disabled.toString());
        return this.http.post<any>(LoyaltySiteManagementService.postDisableLoyaltySiteUrl, null, { params: params });
    }

    public getNextLocationId(): Observable<string> {
        return this.http.post<string>(LoyaltySiteManagementService.nextLocationIdUrl, null, { responseType: 'text' as 'json' });
    }

    public getNewPassword(): Observable<string> {
        return this.http.post<string>(LoyaltySiteManagementService.getNewPasswordUrl, null, { responseType: 'text' as 'json' });
    }

    public getNextUsername(siteId: string): Observable<string> {
        return this.http.post<string>(LoyaltySiteManagementService.getNextUsernameUrl, { SiteId: siteId }, { responseType: 'text' as 'json' });
    }

    public getProvinces(): Observable<any> {
        return this.httpClient.post<any>(`https://elsprod.toget.me/ElP.Application.Platform/Platform/api/Country/GetProvinces`, null, { responseType: 'text' as 'json' });
    }
};


// WEBPACK FOOTER //
// node_modules/angular2-template-loader!./src/app/views/admin/loyalty-site/loyalty-site-management.service.ts